import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Crown, ExternalLink, Globe, Sparkles, Star, TrendingUp, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

// Investor's home — projects ranked by multi-dimensional compatibility:
//   GOLD (≥70) → SILVER (40-69) → BRONZE (<40)
// Each card surfaces the founder score, compatibility %, and a 1-line
// "why this match" rationale.
export default function InvestorFeed() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [feed, setFeed] = useState({
    gold: [], silver: [], bronze: [],
    gold_count: 0, silver_count: 0, bronze_count: 0,
    my_categories: [], stage_focus: [], risk_appetite: "balanced",
  });
  const [myInterests, setMyInterests] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [showBronze, setShowBronze] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/investors?next=/app/investor-feed"); return; }
    if (user.role !== "investor") { navigate("/investors"); return; }
    Promise.all([
      api.get("/investor/feed"),
      api.get("/investor/my-interests"),
    ]).then(([f, i]) => {
      setFeed({ bronze: [], bronze_count: 0, stage_focus: [], risk_appetite: "balanced", ...f.data });
      setMyInterests(new Set(i.data.project_ids || []));
    }).catch(() => toast.error("Could not load feed.")).finally(() => setLoading(false));
  }, [user, authLoading, navigate]);

  const expressInterest = async (project) => {
    try {
      await api.post("/investor/express-interest", { project_id: project.id, message: "" });
      setMyInterests((s) => new Set([...s, project.id]));
      toast.success(`You're connected to ${project.title}.`);
    } catch {
      toast.error("Could not express interest.");
    }
  };

  if (loading) {
    return <div className="border border-dashed border-white/15 p-16 text-center text-white/55">Loading dealflow…</div>;
  }

  const renderCard = (p, tier) => {
    const isGold   = tier === "gold";
    const isBronze = tier === "bronze";
    const expressed = myInterests.has(p.id);
    const fscore = Math.max(0, Math.min(100, Number(p.founder_score) || 0));
    const compat = Math.max(0, Math.min(100, Number(p.compatibility) || 0));
    return (
      <button
        key={p.id}
        type="button"
        onClick={() => setActive(p)}
        className={`group relative text-left border p-5 transition-all ${
          isGold
            ? "bg-gradient-to-br from-[#1a1310] via-[#141416] to-[#141416] border-[#FF9500]/70 shadow-[0_0_40px_-12px_rgba(255,149,0,0.4)] hover:border-[#FF9500]"
            : isBronze
              ? "bg-[#0e0e10] border-white/8 hover:border-white/30 opacity-90"
              : "bg-[#141416] border-white/10 hover:border-white/40"
        }`}
        data-testid={`feed-card-${p.id}`}
      >
        {isGold && (
          <div className="absolute top-3 right-3 inline-flex items-center gap-1 bg-[#FF9500] text-black px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-widest">
            <Crown className="w-3 h-3" /> {compat}% MATCH
          </div>
        )}
        {!isGold && (
          <div className="absolute top-3 right-3 font-mono text-[9px] uppercase tracking-widest text-white/55">
            {compat}% match
          </div>
        )}
        <div className="font-mono text-[10px] uppercase tracking-widest text-white/45 mb-2">
          {p.category} · {p.stage}
        </div>
        <div className={`font-display font-bold text-xl mb-2 ${isGold ? "text-white" : "text-white/85"}`}>{p.title}</div>
        <p className="text-sm text-white/65 mb-3 line-clamp-2">{p.one_liner}</p>

        {/* Founder score pill + why-match rationale */}
        <div className="flex items-center gap-3 mb-3">
          <div
            className={`inline-flex items-center gap-1.5 px-2 py-1 font-mono text-[9px] uppercase tracking-widest border ${
              fscore >= 80 ? "border-[#FF9500] text-[#FF9500]" :
              fscore >= 65 ? "border-white/40 text-white/80" :
                              "border-white/15 text-white/55"
            }`}
            data-testid={`feed-card-${p.id}-founder-score`}
          >
            <Zap className="w-3 h-3" /> {fscore}/100
          </div>
          {p.why_match && (
            <span className="text-[10px] text-white/55 italic line-clamp-1">{p.why_match}</span>
          )}
        </div>

        <div className="pt-3 border-t border-white/10 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest">
          <span className="text-white/45">by {p.founder_name_masked}</span>
          <span className={isGold ? "text-[#FF9500] font-bold" : "text-white/55"}>
            ${(p.funding_usd || 0).toLocaleString()} target
          </span>
        </div>
        {expressed && (
          <div className="mt-2 font-mono text-[9px] uppercase tracking-widest text-[#34C759]">
            ✓ Interest expressed
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="space-y-8" data-testid="investor-feed-page">
      <div>
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500 mb-3">/ INVESTOR FEED</div>
        <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">
          {feed.gold_count > 0
            ? <><span className="text-[#FF9500]">{feed.gold_count}</span> gold-match{feed.gold_count === 1 ? "" : "es"} for you.</>
            : "No gold matches yet — check the silver tier."}
        </h1>
        {feed.my_categories.length > 0 && (
          <p className="mt-3 font-mono text-[10px] uppercase tracking-widest text-white/55 flex items-center gap-2 flex-wrap">
            <Sparkles className="w-3 h-3 text-[#FF9500]" /> Your categories:
            {feed.my_categories.map((c) => (
              <span key={c} className="bg-[#FF9500]/15 text-[#FF9500] px-2 py-0.5">{c}</span>
            ))}
          </p>
        )}
      </div>

      {feed.gold.length > 0 && (
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FF9500] mb-4 flex items-center gap-2">
            <Crown className="w-3 h-3" /> Gold tier · in your categories
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feed-gold-list">
            {feed.gold.map((p) => renderCard(p, "gold"))}
          </div>
        </section>
      )}

      {feed.silver.length > 0 && (
        <section>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/55 mb-4 flex items-center gap-2">
            <Star className="w-3 h-3" /> Silver tier · adjacent opportunities
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feed-silver-list">
            {feed.silver.map((p) => renderCard(p, "silver"))}
          </div>
        </section>
      )}

      {feed.bronze.length > 0 && (
        <section>
          <button
            type="button"
            onClick={() => setShowBronze((v) => !v)}
            className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/45 hover:text-white/80 mb-4 flex items-center gap-2 transition-colors"
            data-testid="feed-bronze-toggle"
          >
            ▸ Bronze tier · low-fit deals ({feed.bronze_count}) {showBronze ? "— hide" : "— show"}
          </button>
          {showBronze && (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="feed-bronze-list">
              {feed.bronze.map((p) => renderCard(p, "bronze"))}
            </div>
          )}
        </section>
      )}

      {feed.gold.length === 0 && feed.silver.length === 0 && feed.bronze.length === 0 && (
        <div className="border border-dashed border-white/15 p-16 text-center text-white/55" data-testid="feed-empty">
          No live projects yet. As founders submit, they'll appear here automatically.
        </div>
      )}

      {/* Project detail modal */}
      {active && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setActive(null)}>
          <div className="bg-[#141416] border border-white/15 max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 lg:p-8">
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#FF9500] mb-3 flex items-center gap-2">
                {active.match_tier === "gold" && <Crown className="w-3 h-3" />}
                {active.category} · {active.stage}
              </div>
              <h2 className="font-display font-black text-3xl tracking-tighter mb-3">{active.title}</h2>
              <p className="text-lg text-white/80 mb-4">{active.one_liner}</p>

              {/* Match summary — compatibility + why */}
              <div className="bg-[#1a1310]/60 border border-[#FF9500]/30 p-4 mb-5" data-testid="feed-modal-match">
                <div className="flex items-center gap-4 mb-2">
                  <div className="font-display font-black text-3xl text-[#FF9500] leading-none">
                    {Math.round(active.compatibility || 0)}%
                  </div>
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/65">
                    Compatibility with your thesis
                  </div>
                </div>
                {active.why_match && (
                  <p className="text-sm text-white/75 italic">{active.why_match}</p>
                )}
              </div>

              {/* Founder score breakdown */}
              {active.score && (
                <div className="mb-5" data-testid="feed-modal-score">
                  <div className="font-mono text-[10px] uppercase tracking-widest text-white/55 mb-3 flex items-center gap-2">
                    <Zap className="w-3 h-3 text-[#FF9500]" /> Founder score · {active.score.overall}/100
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {["idea","team","market","execution","traction","scalability"].map((k) => {
                      const v = Math.max(0, Math.min(100, Number(active.score[k]) || 0));
                      return (
                        <div key={k} className="bg-black/30 p-2">
                          <div className="flex items-baseline justify-between mb-1">
                            <span className="font-mono text-[9px] uppercase tracking-widest text-white/55">{k}</span>
                            <span className="text-white text-sm font-bold">{v}</span>
                          </div>
                          <div className="h-1 bg-white/10">
                            <div className="h-full bg-[#FF9500]" style={{ width: `${v}%` }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {active.score.rationale && (
                    <p className="mt-3 text-xs text-white/55 italic leading-relaxed">"{active.score.rationale}"</p>
                  )}
                </div>
              )}

              {active.description && (
                <div className="bg-black/30 p-5 mb-5 whitespace-pre-wrap text-sm text-white/75 leading-relaxed">
                  {active.description}
                </div>
              )}
              <div className="grid grid-cols-3 gap-4 mb-6 text-[10px] font-mono uppercase tracking-widest">
                <div>
                  <div className="text-white/45 mb-1">Founder</div>
                  <div className="text-white">{active.founder_name_masked}</div>
                </div>
                <div>
                  <div className="text-white/45 mb-1">Target</div>
                  <div className="text-[#FF9500]">${(active.funding_usd || 0).toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-white/45 mb-1">Country</div>
                  <div className="text-white flex items-center gap-1"><Globe className="w-3 h-3" />{active.country || "—"}</div>
                </div>
              </div>
              {active.website && (
                <a href={active.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-[#FF9500] hover:text-white text-sm mb-5">
                  <ExternalLink className="w-3.5 h-3.5" /> {active.website}
                </a>
              )}
              {myInterests.has(active.id) ? (
                <div className="bg-[#34C759]/10 border border-[#34C759]/40 text-[#34C759] px-4 py-3 font-mono text-xs uppercase tracking-widest">
                  ✓ You've expressed interest. The founder has your contact.
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => expressInterest(active)}
                  className="w-full inline-flex items-center justify-center gap-3 bg-[#FF9500] text-black hover:bg-[#FF9500]/85 px-6 py-4 font-bold transition-colors"
                  data-testid="feed-express-interest"
                >
                  <TrendingUp className="w-4 h-4" /> Express interest — share my contact
                </button>
              )}
              <button onClick={() => setActive(null)} className="mt-3 w-full text-white/55 hover:text-white text-sm py-2">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
